import { computed, ref, toValue, type MaybeRefOrGetter, type Ref, type ComputedRef } from 'vue';

type ItemId = number | string;

interface SelectableItem {
  id: ItemId;
}

interface UseItemSelectionOptions<T extends SelectableItem> {
  items?: MaybeRefOrGetter<T[]>;
  selectedIds?: MaybeRefOrGetter<ItemId[]>;
  initialSelection?: ItemId[];
}

interface UseItemSelectionReturn {
  selectedIds: Ref<ItemId[]>;
  allSelected: ComputedRef<boolean>;
  someSelected: ComputedRef<boolean>;
  isIndeterminate: ComputedRef<boolean>;
  selectedCount: ComputedRef<number>;
  isSelected: (id: ItemId) => boolean;
  select: (id: ItemId) => void;
  deselect: (id: ItemId) => void;
  toggle: (id: ItemId) => void;
  selectAll: (items?: SelectableItem[]) => void;
  deselectAll: () => void;
  updateAllSelection: (checked: boolean, items?: SelectableItem[]) => void;
}

export default function useItemSelection<T extends SelectableItem>(
  options: UseItemSelectionOptions<T> = {}
): UseItemSelectionReturn {
  const { items, selectedIds: externalSelectedIds, initialSelection = [] } = options;

  const internalSelectedIds: Ref<ItemId[]> = ref([...initialSelection]);

  const selectedIds = computed({
    get: () => externalSelectedIds ? toValue(externalSelectedIds) : internalSelectedIds.value,
    set: (value: ItemId[]) => {
      internalSelectedIds.value = value;
    }
  });

  const getItems = (): T[] => items ? toValue(items) : [];

  const allSelected = computed(() => {
    const currentItems = getItems();
    if (currentItems.length === 0) {
      return false;
    }
    return currentItems.every((item) => selectedIds.value.includes(item.id));
  });

  const someSelected = computed(() => {
    return selectedIds.value.length > 0;
  });

  const isIndeterminate = computed(() => {
    const currentItems = getItems();
    if (currentItems.length === 0) {
      return selectedIds.value.length > 0;
    }
    return someSelected.value && !allSelected.value;
  });

  const selectedCount = computed(() => selectedIds.value.length);

  const isSelected = (id: ItemId): boolean => {
    return selectedIds.value.includes(id);
  };

  const select = (id: ItemId): void => {
    if (!internalSelectedIds.value.includes(id)) {
      internalSelectedIds.value.push(id);
    }
  };

  const deselect = (id: ItemId): void => {
    const index = internalSelectedIds.value.indexOf(id);
    if (index >= 0) {
      internalSelectedIds.value.splice(index, 1);
    }
  };

  const toggle = (id: ItemId): void => {
    if (isSelected(id)) {
      deselect(id);
    } else {
      select(id);
    }
  };

  const selectAll = (itemsToSelect?: SelectableItem[]): void => {
    const targetItems = itemsToSelect ?? getItems();
    targetItems.forEach((item) => {
      if (!internalSelectedIds.value.includes(item.id)) {
        internalSelectedIds.value.push(item.id);
      }
    });
  };

  const deselectAll = (): void => {
    internalSelectedIds.value = [];
  };

  const updateAllSelection = (checked: boolean, itemsToUpdate?: SelectableItem[]): void => {
    const targetItems = itemsToUpdate ?? getItems();
    targetItems.forEach((item) => {
      if (checked) {
        select(item.id);
      } else {
        deselect(item.id);
      }
    });
  };

  return {
    selectedIds: internalSelectedIds,
    allSelected,
    someSelected,
    isIndeterminate,
    selectedCount,
    isSelected,
    select,
    deselect,
    toggle,
    selectAll,
    deselectAll,
    updateAllSelection,
  };
}
